import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Activity, Stethoscope, Check } from 'lucide-react';
import { VITAL_FIELDS, CONSULT_DEPARTMENTS, SPECIALISTS, SPECIALIST_INTERNAL_DEPT } from '../pages/workflow/VisitBits';

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  withReason = false,
  reasonRequired = false,
  withVitals = false,
  withDepartments = false,
  initialPhone = '',
  preauthRequired = false,
  reasonLabel = 'Reason (optional)',
  reasonPlaceholder = 'Enter a reason…',
  confirmLabel = 'Confirm',
  destructive = false,
  onConfirm,
}) {
  const [reason, setReason] = useState('');
  const [vitals, setVitals] = useState({});
  const [departments, setDepartments] = useState([]);
  const [client, setClient] = useState({ location: '', phone: '' });
  const [specialist, setSpecialist] = useState('');
  const [preauthCode, setPreauthCode] = useState('');
  useEffect(() => {
    if (open) { setReason(''); setVitals({}); setDepartments(withDepartments && !preauthRequired ? ['general'] : []); setClient({ location: '', phone: initialPhone || '' }); setSpecialist(''); setPreauthCode(''); }
  }, [open, initialPhone, preauthRequired, withDepartments]);

  const toggleDept = (key) => setDepartments((prev) => (prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key]));
  const chooseSpecialist = (name) => {
    setSpecialist(name);
    setDepartments((prev) => (name ? (prev.includes('specialist') ? prev : [...prev, 'specialist']) : prev.filter((d) => d !== 'specialist')));
  };
  // Non pre-auth hospitals: General Doctor only. Pre-auth hospitals: all departments + specialist dropdown.
  const deptButtons = CONSULT_DEPARTMENTS.filter((d) => !d.preauthOnly && (preauthRequired || d.key === 'general'));
  const nameOf = (k) => (k === 'specialist' ? specialist || 'Specialist' : CONSULT_DEPARTMENTS.find((d) => d.key === k)?.label);
  const phoneOk = client.phone.replace(/\D/g, '').length >= 7;
  const codeOk = !preauthRequired || preauthCode.replace(/\s/g, '').length >= 6;
  const confirmDisabled = (withDepartments && (departments.length === 0 || !client.location.trim() || !phoneOk || !codeOk)) || (withReason && reasonRequired && !reason.trim());

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onOpenChange(false);
    if (onConfirm) onConfirm(reason, vitals, departments, { location: client.location.trim(), phone: client.phone.trim(), specialist: specialist || null, preauth_code: preauthCode.trim().toUpperCase() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={withVitals ? 'sm:max-w-lg' : 'sm:max-w-md'} data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription className="whitespace-pre-line">{description}</DialogDescription>}
        </DialogHeader>
        {withDepartments && preauthRequired && (
          <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3" data-testid="checkin-preauth">
            <Label className="text-sm font-semibold text-amber-900">Pre-authorization code from the Acacia Office <span className="text-red-500">*</span></Label>
            <Input value={preauthCode} onChange={(e) => setPreauthCode(e.target.value.toUpperCase())} placeholder="PA-XXXXXX (printed on the stamped form)" className="mt-1 font-mono tracking-widest uppercase bg-white" data-testid="checkin-preauth-code" />
            <p className="text-xs text-amber-800 mt-1">This hospital requires pre-authorization. Enter the code on the patient's stamped form; a valid code sends the patient straight to the doctor.</p>
          </div>
        )}
        {withDepartments && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="checkin-client-details">
            <div>
              <label className="text-sm font-medium text-gray-700">Client location <span className="text-red-500">*</span></label>
              <Input value={client.location} onChange={(e) => setClient((p) => ({ ...p, location: e.target.value }))} placeholder="e.g. Munuki, Juba" className="mt-1" data-testid="checkin-client-location" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Phone number <span className="text-red-500">*</span></label>
              <Input value={client.phone} onChange={(e) => setClient((p) => ({ ...p, phone: e.target.value }))} placeholder="e.g. 0924 000 000" inputMode="tel" className={`mt-1 ${client.phone && !phoneOk ? 'border-red-400' : ''}`} data-testid="checkin-client-phone" />
              {client.phone && !phoneOk && <p className="text-xs text-red-600 mt-0.5">Enter at least 7 digits</p>}
            </div>
          </div>
        )}
        {withDepartments && (
          <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3" data-testid="checkin-departments">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
              <Stethoscope className="w-4 h-4 text-blue-600" /> {preauthRequired ? 'Send patient to' : 'First Line of Treatment is General Doctor'}{preauthRequired && <span className="text-red-500">*</span>}
            </p>
            <div className="flex flex-wrap gap-2">
              {deptButtons.map((d) => {
                const pos = departments.indexOf(d.key);
                const on = pos >= 0;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDept(d.key)}
                    aria-pressed={on}
                    data-testid={`checkin-dept-${d.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? 'bg-blue-600 text-white border-blue-600 shadow' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-700'}`}
                  >
                    {on && <Check className="w-3.5 h-3.5" />}
                    {d.label}
                    {on && departments.length > 1 && <span className="ml-0.5 text-[10px] bg-white/25 rounded-full px-1.5">{pos + 1}</span>}
                  </button>
                );
              })}
            </div>
            {preauthRequired && (
              <div className="mt-3" data-testid="checkin-specialist-wrap">
                <label className="text-sm font-medium text-gray-700">Specialist referral (optional{departments.includes('specialist') ? ` — position ${departments.indexOf('specialist') + 1}` : ''})</label>
                <select value={specialist} onChange={(e) => chooseSpecialist(e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="checkin-specialist">
                  <option value="">No specialist</option>
                  {SPECIALISTS.filter((s) => !SPECIALIST_INTERNAL_DEPT[s]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Covered by the pre-authorization code issued by the Acacia Office.</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {departments.length > 1
                ? `Patient will be seen in this order: ${departments.map(nameOf).join(' → ')}`
                : preauthRequired ? 'Pick one or more. If more than one, the patient sees them in the order you click.' : ''}
            </p>
          </div>
        )}
        {withVitals && (
          <div className="border border-teal-200 bg-teal-50/40 rounded-lg p-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
              <Activity className="w-4 h-4 text-teal-600" /> Vital Signs (optional — the doctor will see them)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {VITAL_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-xs text-gray-600">{f.label}</label>
                  <Input
                    value={vitals[f.key] || ''}
                    onChange={(e) => setVitals((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="h-8 text-sm mt-0.5 bg-white"
                    data-testid={`checkin-vitals-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {withReason && (
          <div>
            <label className="text-sm font-medium text-gray-700">{reasonLabel}</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={reasonPlaceholder}
              className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-teal-500 focus:outline-none"
              data-testid="confirm-dialog-reason"
              autoFocus
            />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="confirm-dialog-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700'}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
